/**
 * Dashboard layout: hosts the sidebar (a single render tree driven by
 * permissions) and the main outlet. Also owns the global polling lifecycle and
 * the user/theme footer.
 */

import { Suspense, useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { clearToken, getEmail, getRole } from "@/lib/auth";
import PatDialog from "@/components/PatDialog";
import { useTheme } from "@/hooks/useTheme";
import { GlasstapeWordmark, GlasstapeMark } from "@/components/ds";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { Sidebar } from "./sidebar/Sidebar";
import s from "./sidebar.module.sass";
import dl from "./dashboard-layout.module.sass";
import cn from "classnames";

export default function DashboardLayout() {
  const { theme, toggleTheme } = useTheme();
  const { logout: auth0Logout } = useAuth0();
  const userEmail = getEmail();
  const role = getRole();

  const [showPat, setShowPat] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("gt-sidebar-collapsed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("gt-sidebar-collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  const startPolling = useGlobalStore((st) => st.startPolling);
  const stopPolling = useGlobalStore((st) => st.stopPolling);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  function handleLogout() {
    clearToken();
    auth0Logout({ logoutParams: { returnTo: `${window.location.origin}/login` } });
  }

  return (
    <div className={dl.layout}>
      <aside className={cn(s.sidebar, collapsed && s.collapsed)}>
        <div className={s.header}>
          <Link to="/" className={s.logo} aria-label="Glasstape, home">
            <span className={s.logoText}>
              {collapsed ? <GlasstapeMark size={26} /> : <GlasstapeWordmark size={24} />}
            </span>
          </Link>
          <button
            type="button"
            className={s.collapseToggle}
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 220ms ease" }}
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>

        <Sidebar collapsed={collapsed} />

        {showPat && <PatDialog onClose={() => setShowPat(false)} />}

        {showUserMenu && (
          <div className={s.userPanel}>
            <button
              className={s.userPanelAction}
              onClick={() => { setShowPat(true); setShowUserMenu(false); }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="15" r="4" />
                <path d="M12 11l8-8" />
                <path d="M19 3l2 2-1 1-2-2" />
                <path d="M17 5l2 2" />
              </svg>
              Generate API token
            </button>
            <button
              className={cn(s.userPanelAction, s.userPanelLogout)}
              onClick={handleLogout}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign out
            </button>
          </div>
        )}

        <div className={s.footer}>
          <button className={s.userTrigger} onClick={() => setShowUserMenu(v => !v)}>
            <span className={s.userAvatar}>{userEmail?.[0]?.toUpperCase() ?? '?'}</span>
            <div className={s.userTriggerInfo}>
              <span className={s.userTriggerEmail}>{userEmail}</span>
              {role && <span className={s.userTriggerRole}>{role}</span>}
            </div>
            <svg className={cn(s.userChevron, showUserMenu && s.open)} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
          <button
            className={s.themeToggle}
            onClick={toggleTheme}
            title={theme === "light" ? "Dark mode" : "Light mode"}
          >
            {theme === "light" ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            )}
          </button>
        </div>
      </aside>

      <main className={dl.main}>
        {/* One boundary for every route chunk. It sits inside the layout so a
            page load swaps the content and leaves the sidebar in place. */}
        <Suspense fallback={<div className={dl.routeFallback} aria-busy="true" />}>
          <Outlet context={{ refreshAlerts: () => {} }} />
        </Suspense>
      </main>
    </div>
  );
}
